/**
 * What a new ingestion rule starts from when another page sends someone to
 * write one: a file's label ("Add a rule for *.csv") or a hand-written
 * ingestion trigger ("Convert to ingestion rule"). Travels in the new-rule
 * page's URL so the link can be shared.
 */

import { fileSelectorsOf, type InvocationInput } from "@/shared/domain/actions";
import { basename, extname } from "@/shared/domain/files/filesystem/path";
import {
  isSchedule,
  PlatformEventType,
  isTriggeringDatasetId,
  TRIGGERING_DATASET_ID,
  type ActionInvocationSpec,
  type InvokeActionTarget,
  type TriggerSource,
} from "@/shared/domain/triggers";

export interface IngestionRulePrefill {
  name?: string;
  includePatterns: string[];
  excludePatterns: string[];
  invocation?: ActionInvocationSpec;
  /** The hand-written trigger the rule replaces, offered for disabling once the rule is saved. */
  sourceTriggerId?: string;
  /**
   * The `!` patterns a converted trigger listed, which became the rule's
   * excludes. A trigger matches each of its patterns on its own, so a lone
   * `!x` never kept a file out; the rule will, and the new-rule page says so.
   */
  triggerExclusions?: string[];
}

const PARAMS = {
  name: "name",
  include: "include",
  exclude: "exclude",
  invocation: "invocation",
  sourceTrigger: "fromTrigger",
  triggerExclusion: "triggerExclusion",
} as const;

/** The file's extension without the dot, or null when it has none (a leading dot does not count). */
export function fileExtension(relativePath: string): string | null {
  const name = basename(relativePath);
  const extension = extname(name);
  return extension === "" || extension === name ? null : extension.slice(1);
}

/**
 * A rule for every file of this one's kind: `**\/*.csv` named "CSV files", or,
 * for a file with no extension, a rule for that file name.
 */
export function rulePrefillForFile(relativePath: string): IngestionRulePrefill {
  const extension = fileExtension(relativePath);
  if (extension === null) {
    const name = basename(relativePath);
    return { name, includePatterns: [`**/${name}`], excludePatterns: [] };
  }
  return {
    name: `${extension.toUpperCase()} files`,
    includePatterns: [`**/*.${extension}`],
    excludePatterns: [],
  };
}

/** The glob a person reads on the "Add a rule" button: `*.csv`, or the bare file name. */
export function filePatternLabel(relativePath: string): string {
  const extension = fileExtension(relativePath);
  return extension === null ? basename(relativePath) : `*.${extension}`;
}

/**
 * Whether a trigger fires when files arrive, which is when an ingestion
 * action on it would be better written as an ingestion rule.
 */
export function firesOnFileArrival(source: TriggerSource): boolean {
  return (
    !isSchedule(source) &&
    source.events.some(
      (event) =>
        event === PlatformEventType.FileUploaded ||
        event === PlatformEventType.UploadCompleted,
    )
  );
}

/**
 * A new rule that does what one trigger target does.
 *
 * The target's file patterns become the rule's, `!`-prefixed ones its
 * excludes. A dataset firing has no file patterns: it reads its files through
 * a selector on the dataset that fired, so that selector's patterns become the
 * rule's and the selector is dropped, since the rule hands the action the
 * uploaded file itself. Additional input patterns become a selector on the
 * fired dataset. Everything else about the invocation carries over.
 */
export function rulePrefillFromTrigger(
  trigger: { name: string; trigger_id?: string },
  target: InvokeActionTarget,
): IngestionRulePrefill {
  const includePatterns = target.required_inputs.filter(
    (pattern) => !pattern.startsWith("!"),
  );
  const excludePatterns = target.required_inputs
    .filter((pattern) => pattern.startsWith("!"))
    .map((pattern) => pattern.slice(1));

  let selectors = fileSelectorsOf(target.invocation_input);
  if (includePatterns.length === 0) {
    const fired = selectors.find((selector) =>
      isTriggeringDatasetId(selector.dataset_id),
    );
    if (fired !== undefined) {
      includePatterns.push(...(fired.paths ?? ["**/*"]));
      selectors = selectors.filter((selector) => selector !== fired);
    }
  }
  // A rule takes no file patterns beyond its own, so the trigger's additional
  // input patterns become a selector on the fired dataset; the action's input
  // resolver lists that dataset's files with the same patterns either way.
  const additionalPatterns = target.additional_inputs ?? [];
  if (additionalPatterns.length > 0) {
    selectors = [
      ...selectors,
      {
        query: null,
        ids: null,
        names: null,
        dataset_id: TRIGGERING_DATASET_ID,
        paths: additionalPatterns,
      },
    ];
  }
  const input = target.invocation_input;
  const hasOtherInputs =
    input !== null &&
    input !== undefined &&
    (input.topics !== null || (input.sessions ?? null) !== null);
  let invocationInput: InvocationInput | null = null;
  if (selectors.length > 0 || hasOtherInputs) {
    let files: InvocationInput["files"] = null;
    if (selectors.length === 1) {
      files = selectors[0];
    } else if (selectors.length > 1) {
      files = selectors;
    }
    invocationInput = {
      files,
      topics: input?.topics ?? null,
      sessions: input?.sessions ?? null,
    };
  }

  return {
    name: trigger.name,
    includePatterns,
    excludePatterns,
    invocation: {
      action: target.action,
      parameter_values: target.parameter_values,
      compute_requirement_overrides:
        target.compute_requirement_overrides ?? null,
      container_parameter_overrides:
        target.container_parameter_overrides ?? null,
      timeout: target.timeout ?? null,
      upload_destination: target.upload_destination ?? null,
      invocation_input: invocationInput,
    },
    ...(trigger.trigger_id !== undefined
      ? { sourceTriggerId: trigger.trigger_id }
      : {}),
    ...(excludePatterns.length > 0
      ? { triggerExclusions: [...excludePatterns] }
      : {}),
  };
}

/** The prefill as the new-rule page's search params. */
export function rulePrefillToSearchParams(
  prefill: IngestionRulePrefill,
): URLSearchParams {
  const params = new URLSearchParams();
  if (prefill.name !== undefined) {
    params.set(PARAMS.name, prefill.name);
  }
  prefill.includePatterns.forEach((pattern) =>
    params.append(PARAMS.include, pattern),
  );
  prefill.excludePatterns.forEach((pattern) =>
    params.append(PARAMS.exclude, pattern),
  );
  if (prefill.invocation !== undefined) {
    params.set(PARAMS.invocation, JSON.stringify(prefill.invocation));
  }
  if (prefill.sourceTriggerId !== undefined) {
    params.set(PARAMS.sourceTrigger, prefill.sourceTriggerId);
  }
  prefill.triggerExclusions?.forEach((pattern) =>
    params.append(PARAMS.triggerExclusion, pattern),
  );
  return params;
}

function parseInvocation(raw: string | null): ActionInvocationSpec | undefined {
  if (raw === null) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<ActionInvocationSpec> | null;
    if (
      parsed === null ||
      typeof parsed !== "object" ||
      typeof parsed.action?.name !== "string"
    ) {
      return undefined;
    }
    return {
      ...parsed,
      parameter_values: parsed.parameter_values ?? {},
    } as ActionInvocationSpec;
  } catch {
    return undefined;
  }
}

/** The prefill a new-rule URL carries, or null when it carries none. */
export function rulePrefillFromSearchParams(
  params: URLSearchParams,
): IngestionRulePrefill | null {
  const prefill: IngestionRulePrefill = {
    includePatterns: params.getAll(PARAMS.include),
    excludePatterns: params.getAll(PARAMS.exclude),
  };
  const name = params.get(PARAMS.name);
  if (name !== null) {
    prefill.name = name;
  }
  const invocation = parseInvocation(params.get(PARAMS.invocation));
  if (invocation !== undefined) {
    prefill.invocation = invocation;
  }
  const sourceTriggerId = params.get(PARAMS.sourceTrigger);
  if (sourceTriggerId !== null && sourceTriggerId !== "") {
    prefill.sourceTriggerId = sourceTriggerId;
  }
  const triggerExclusions = params.getAll(PARAMS.triggerExclusion);
  if (triggerExclusions.length > 0) {
    prefill.triggerExclusions = triggerExclusions;
  }
  const empty =
    prefill.name === undefined &&
    prefill.includePatterns.length === 0 &&
    prefill.excludePatterns.length === 0 &&
    prefill.invocation === undefined &&
    prefill.sourceTriggerId === undefined;
  return empty ? null : prefill;
}
